// FILE: storeSelectors.ts
// Purpose: Stable Zustand selectors for entity lookups and lightweight sidebar projections.
// Exports: Selector factories used by routes and sidebar-heavy components.

import type { OrchestrationThreadHistoryCursor, ProjectId, ThreadId } from "@taskin-code/contracts";

import { DELEGATED_SUBAGENT_ROLE } from "@taskin-code/shared/delegation";
import { SIDE_CHAT_THREAD_ROLE } from "@taskin-code/shared/sideChat";

import {
  deriveDelegatedSubagentTasks,
  deriveDelegationsAwaitingParent,
  deriveDelegationStateByParent,
  type ActiveSubagentTask,
  type DelegationState,
} from "./session-logic";
import type { AppState } from "./store";
import { getThreadFromState, getThreadsFromState } from "./threadDerivation";
import type { Project, SidebarThreadSummary, Thread } from "./types";

function createStableEntitySelector<T extends { id: string }>(
  selectItems: (state: AppState) => readonly T[],
  id: string | null | undefined,
): (state: AppState) => T | undefined {
  let previousItems: readonly T[] | undefined;
  let previousMatch: T | undefined;

  return (state) => {
    if (!id) {
      return undefined;
    }

    const items = selectItems(state);
    if (items === previousItems) {
      return previousMatch;
    }

    previousItems = items;
    previousMatch = items.find((item) => item.id === id);
    return previousMatch;
  };
}

export function createProjectSelector(
  projectId: ProjectId | null | undefined,
): (state: AppState) => Project | undefined {
  return createStableEntitySelector((state) => state.projects, projectId);
}

export function createThreadSelector(
  threadId: ThreadId | null | undefined,
): (state: AppState) => Thread | undefined {
  return (state) =>
    threadId
      ? (getThreadFromState(state, threadId) ??
        state.threads.find((thread) => thread.id === threadId))
      : undefined;
}

export function createAllThreadsSelector(): (state: AppState) => readonly Thread[] {
  let previousThreadIds: readonly ThreadId[] | undefined;
  let previousThreadShellById = {} as AppState["threadShellById"];
  let previousThreadSessionById = {} as AppState["threadSessionById"];
  let previousThreadTurnStateById = {} as AppState["threadTurnStateById"];
  let previousMessageIdsByThreadId = {} as AppState["messageIdsByThreadId"];
  let previousMessageByThreadId = {} as AppState["messageByThreadId"];
  let previousActivityIdsByThreadId = {} as AppState["activityIdsByThreadId"];
  let previousActivityByThreadId = {} as AppState["activityByThreadId"];
  let previousProposedPlanIdsByThreadId = {} as AppState["proposedPlanIdsByThreadId"];
  let previousProposedPlanByThreadId = {} as AppState["proposedPlanByThreadId"];
  let previousTurnDiffIdsByThreadId = {} as AppState["turnDiffIdsByThreadId"];
  let previousTurnDiffSummaryByThreadId = {} as AppState["turnDiffSummaryByThreadId"];
  let previousThreads: readonly Thread[] = [];

  return (state) => {
    if (
      previousThreadIds === state.threadIds &&
      previousThreadShellById === state.threadShellById &&
      previousThreadSessionById === state.threadSessionById &&
      previousThreadTurnStateById === state.threadTurnStateById &&
      previousMessageIdsByThreadId === state.messageIdsByThreadId &&
      previousMessageByThreadId === state.messageByThreadId &&
      previousActivityIdsByThreadId === state.activityIdsByThreadId &&
      previousActivityByThreadId === state.activityByThreadId &&
      previousProposedPlanIdsByThreadId === state.proposedPlanIdsByThreadId &&
      previousProposedPlanByThreadId === state.proposedPlanByThreadId &&
      previousTurnDiffIdsByThreadId === state.turnDiffIdsByThreadId &&
      previousTurnDiffSummaryByThreadId === state.turnDiffSummaryByThreadId
    ) {
      return previousThreads;
    }

    previousThreadIds = state.threadIds;
    previousThreadShellById = state.threadShellById;
    previousThreadSessionById = state.threadSessionById;
    previousThreadTurnStateById = state.threadTurnStateById;
    previousMessageIdsByThreadId = state.messageIdsByThreadId;
    previousMessageByThreadId = state.messageByThreadId;
    previousActivityIdsByThreadId = state.activityIdsByThreadId;
    previousActivityByThreadId = state.activityByThreadId;
    previousProposedPlanIdsByThreadId = state.proposedPlanIdsByThreadId;
    previousProposedPlanByThreadId = state.proposedPlanByThreadId;
    previousTurnDiffIdsByThreadId = state.turnDiffIdsByThreadId;
    previousTurnDiffSummaryByThreadId = state.turnDiffSummaryByThreadId;
    previousThreads = getThreadsFromState(state);
    return previousThreads;
  };
}

export function createThreadProjectIdSelector(
  threadId: ThreadId | null | undefined,
): (state: AppState) => ProjectId | null {
  const selectThread = createThreadSelector(threadId);
  return (state) => selectThread(state)?.projectId ?? null;
}

export function createThreadExistsSelector(
  threadId: ThreadId | null | undefined,
): (state: AppState) => boolean {
  const selectThread = createThreadSelector(threadId);
  return (state) => selectThread(state) !== undefined;
}

/**
 * True once the thread's transcript snapshot has been applied. Threads that only
 * exist as a shell row (just clicked, snapshot still in flight) read as `false`,
 * which is what lets the transcript show a spinner instead of an empty state.
 */
export function createThreadDetailHydratedSelector(
  threadId: ThreadId | null | undefined,
): (state: AppState) => boolean {
  return (state) => (threadId ? (state.threadDetailHydratedById?.[threadId] ?? false) : false);
}

/**
 * Whether the thread has history the store has not loaded yet.
 *
 * Only the server can answer this: the transcript stops at the newest page, and
 * "there is nothing above" and "there is more above" look identical from the
 * rows alone.
 */
export function createThreadHasMoreHistorySelector(
  threadId: ThreadId | null | undefined,
): (state: AppState) => boolean {
  return (state) => (threadId ? (state.hasMoreHistoryByThreadId?.[threadId] ?? false) : false);
}

export function createThreadOldestHistoryCursorSelector(
  threadId: ThreadId | null | undefined,
): (state: AppState) => OrchestrationThreadHistoryCursor | undefined {
  return (state) => (threadId ? state.oldestHistoryCursorByThreadId?.[threadId] : undefined);
}

export function createSidebarThreadSummarySelector(
  threadId: ThreadId | null | undefined,
): (state: AppState) => SidebarThreadSummary | undefined {
  return (state) => (threadId ? state.sidebarThreadSummaryById[threadId] : undefined);
}

/**
 * Every thread this person's own lists may draw.
 *
 * Foreign threads — the ones they opened from the directory — are held in the
 * same store as their own but belong to no list of theirs. Dropping them here
 * covers the sidebar, Pospuestos and everything else built on these summaries in
 * one place, which is the point: the alternative is remembering the rule at
 * every call site, and forgetting it once puts somebody else's thread back in
 * the sidebar this whole change exists to clear.
 */
export function createSidebarThreadSummariesSelector(): (
  state: AppState,
) => readonly SidebarThreadSummary[] {
  let previousThreadIds: readonly ThreadId[] | undefined;
  let previousSummaryById: Record<string, SidebarThreadSummary> | undefined;
  let previousForeignThreadIds: ReadonlySet<ThreadId> | undefined;
  let previousSummaries: readonly SidebarThreadSummary[] = [];

  return (state) => {
    const threadIds = state.threadIds ?? state.threads.map((thread) => thread.id);
    const foreignThreadIds = state.foreignThreadIds;
    if (
      threadIds === previousThreadIds &&
      state.sidebarThreadSummaryById === previousSummaryById &&
      foreignThreadIds === previousForeignThreadIds
    ) {
      return previousSummaries;
    }

    previousThreadIds = threadIds;
    previousSummaryById = state.sidebarThreadSummaryById;
    previousForeignThreadIds = foreignThreadIds;
    previousSummaries = threadIds.flatMap((threadId) => {
      if (foreignThreadIds?.has(threadId)) return [];
      const summary = state.sidebarThreadSummaryById[threadId];
      return summary ? [summary] : [];
    });
    return previousSummaries;
  };
}

/**
 * The threads a person may open: every live root thread, plus the children that
 * are threads in their own right — delegations and side chats.
 *
 * Every surface that offers a thread to open goes through this. It started as
 * the sidebar's rule and stayed the sidebar's alone, which is how the split
 * view's "Choose Chat" ended up listing archived threads and the providers'
 * synthetic `subagent:<parent>:<toolUseId>` sidechains next to real chats — the
 * user's own threads, but ones no other screen shows and none of them anything
 * they had opened. One rule, one place.
 *
 * A delegation of a managed agent is a thread of its own, with its own
 * transcript, and the sidebar tree is the only place the user can reach it — so
 * it stays visible even after the delegating agent archives it, which is what
 * closing a delegation does. A side chat qualifies for the same reason: the user
 * opened it, it holds a real conversation, and once its panel is closed the tree
 * is the only way back to it. What stays out are the synthetic children the
 * providers mint per sidechain (`subagent:<parent>:<toolUseId>`): they mirror
 * work already told in the parent's own transcript.
 *
 * A child is only shown while its whole ancestry is: one whose parent is
 * archived or gone would otherwise become a root row of its own.
 */
export function createSidebarDisplayThreadsSelector(): (
  state: AppState,
) => readonly SidebarThreadSummary[] {
  const selectSidebarSummaries = createSidebarThreadSummariesSelector();
  let previousSummaries: readonly SidebarThreadSummary[] | undefined;
  let previousDisplaySummaries: readonly SidebarThreadSummary[] = [];

  return (state) => {
    const sidebarSummaries = selectSidebarSummaries(state);
    if (sidebarSummaries === previousSummaries) {
      return previousDisplaySummaries;
    }

    previousSummaries = sidebarSummaries;
    previousDisplaySummaries = filterOpenableThreads(sidebarSummaries);
    return previousDisplaySummaries;
  };
}

/**
 * Child roles that name a thread the user can open. Anything else is a
 * provider-minted sidechain, which the parent's transcript already tells.
 */
const SIDEBAR_VISIBLE_SUBAGENT_ROLES: ReadonlyArray<string> = [
  DELEGATED_SUBAGENT_ROLE,
  SIDE_CHAT_THREAD_ROLE,
];

export function filterOpenableThreads<
  T extends Pick<SidebarThreadSummary, "id" | "parentThreadId" | "subagentRole" | "archivedAt">,
>(threads: readonly T[]): T[] {
  const threadById = new Map(threads.map((thread) => [thread.id, thread] as const));
  const visibilityById = new Map<ThreadId, boolean>();

  const isVisible = (thread: T, seen: Set<ThreadId>): boolean => {
    const cached = visibilityById.get(thread.id);
    if (cached !== undefined) {
      return cached;
    }
    // A cycle cannot happen in a parent chain the server builds, but resolving
    // one recursively would hang the sidebar, so it is treated as invisible.
    if (seen.has(thread.id)) {
      return false;
    }
    seen.add(thread.id);

    const parentThreadId = thread.parentThreadId ?? null;
    const parent = parentThreadId ? threadById.get(parentThreadId) : undefined;
    const visible = parentThreadId
      ? SIDEBAR_VISIBLE_SUBAGENT_ROLES.includes(thread.subagentRole ?? "") &&
        parent !== undefined &&
        isVisible(parent, seen)
      : thread.archivedAt == null;

    visibilityById.set(thread.id, visible);
    return visible;
  };

  return threads.filter((thread) => isVisible(thread, new Set()));
}

function sameActiveSubagentTasks(
  left: ReadonlyArray<ActiveSubagentTask>,
  right: ReadonlyArray<ActiveSubagentTask>,
): boolean {
  return (
    left.length === right.length &&
    left.every((task, index) => {
      const other = right[index];
      return (
        other !== undefined &&
        task.key === other.key &&
        task.threadId === other.threadId &&
        task.subagentType === other.subagentType &&
        task.description === other.description &&
        task.startedAt === other.startedAt &&
        task.awaitingParent === other.awaitingParent
      );
    })
  );
}

function sameDelegationState(left: DelegationState, right: DelegationState): boolean {
  return (
    left.liveCount === right.liveCount &&
    left.hasLiveDescendants === right.hasLiveDescendants &&
    left.oldestLiveStartedAt === right.oldestLiveStartedAt &&
    left.readyCount === right.readyCount
  );
}

function sameDelegationStateByParent(
  left: ReadonlyMap<ThreadId, DelegationState>,
  right: ReadonlyMap<ThreadId, DelegationState>,
): boolean {
  if (left.size !== right.size) return false;
  for (const [threadId, state] of left) {
    const other = right.get(threadId);
    if (!other || !sameDelegationState(state, other)) return false;
  }
  return true;
}

/**
 * Delegation state for every thread that has delegations, keyed by parent.
 *
 * Built from the sidebar summaries rather than from shells and sessions: a
 * summary already carries every fact a delegation is judged by (parent, role,
 * archival, session, latest turn), it is rebuilt only when one of those changes,
 * and reading it keeps one answer for the sidebar and the chat instead of two
 * derivations that could disagree.
 *
 * One selector for the whole sidebar rather than one per row: rows are drawn per
 * thread, and answering this per row would rescan the collection each time. The
 * result keeps its identity while the numbers hold, so a turn advancing
 * somewhere unrelated does not rerender every row.
 */
export function createDelegationStateByParentSelector(): (
  state: AppState,
) => ReadonlyMap<ThreadId, DelegationState> {
  const selectSidebarSummaries = createSidebarThreadSummariesSelector();
  let previousSummaries: readonly SidebarThreadSummary[] | undefined;
  let previousResult: ReadonlyMap<ThreadId, DelegationState> = new Map();

  return (state) => {
    const summaries = selectSidebarSummaries(state);
    if (summaries === previousSummaries) {
      return previousResult;
    }
    previousSummaries = summaries;

    const nextResult = deriveDelegationStateByParent(summaries);
    if (sameDelegationStateByParent(previousResult, nextResult)) {
      return previousResult;
    }

    previousResult = nextResult;
    return previousResult;
  };
}

/**
 * Shared plumbing for the two per-thread delegation lists: both are a projection
 * of the same summaries, and both must keep their identity across unrelated
 * updates or the chat indicator rerenders on every token.
 */
function createDelegationListSelector(
  parentThreadId: ThreadId | null,
  derive: (
    threads: ReadonlyArray<SidebarThreadSummary>,
    parentThreadId: ThreadId,
  ) => ReadonlyArray<ActiveSubagentTask>,
): (state: AppState) => ReadonlyArray<ActiveSubagentTask> {
  const selectSidebarSummaries = createSidebarThreadSummariesSelector();
  let previousSummaries: readonly SidebarThreadSummary[] | undefined;
  let previousResult: ReadonlyArray<ActiveSubagentTask> = [];

  return (state) => {
    if (!parentThreadId) {
      return previousResult.length === 0 ? previousResult : (previousResult = []);
    }

    const summaries = selectSidebarSummaries(state);
    if (summaries === previousSummaries) {
      return previousResult;
    }
    previousSummaries = summaries;

    const nextResult = derive(summaries, parentThreadId);
    if (sameActiveSubagentTasks(previousResult, nextResult)) {
      return previousResult;
    }

    previousResult = nextResult;
    return previousResult;
  };
}

/** The delegations of one thread that are running right now. */
export function createDelegatedSubagentTasksSelector(
  parentThreadId: ThreadId | null,
): (state: AppState) => ReadonlyArray<ActiveSubagentTask> {
  return createDelegationListSelector(parentThreadId, deriveDelegatedSubagentTasks);
}

/**
 * The delegations of one thread that already answered and are waiting for it to
 * read them.
 */
export function createDelegationsAwaitingParentSelector(
  parentThreadId: ThreadId | null,
): (state: AppState) => ReadonlyArray<ActiveSubagentTask> {
  return createDelegationListSelector(parentThreadId, deriveDelegationsAwaitingParent);
}

export function createFirstProjectSelector(): (state: AppState) => Project | undefined {
  let previousProjects: readonly Project[] | undefined;
  let previousFirstProject: Project | undefined;

  return (state) => {
    if (state.projects === previousProjects) {
      return previousFirstProject;
    }

    previousProjects = state.projects;
    previousFirstProject = state.projects[0];
    return previousFirstProject;
  };
}
